import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  UserPlus,
  User,
  Mail,
  Phone,
  Briefcase,
  Building2,
  Clock3,
  IndianRupee,
  CalendarDays,
  MapPin,
  ArrowLeft,
  Save,
} from "lucide-react";

import "./admin.css";

function EmployeeRegister() {
  const navigate = useNavigate();

  // ==============================
  // Employee Form State
  // ==============================
  const [employee, setEmployee] = useState({
    name: "",
    email: "",
    phone: "",
    designation: "",
    departmentId: "",
    experience: "",
    salary: "",
    joiningDate: "",
    location: "",
  });

  // ==============================
  // Department State
  // ==============================
  const [departments, setDepartments] = useState([]);

  const [departmentLoading, setDepartmentLoading] = useState(true);

  const [loading, setLoading] = useState(false);

  // ==============================
  // Fetch Departments
  // ==============================
  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setDepartmentLoading(true);

      const response = await fetch(
        "/api/departments"
      );

      if (!response.ok) {
        throw new Error("Failed to fetch departments");
      }

      const data = await response.json();

      console.log("DEPARTMENT API RESPONSE:", data);

      /*
       * Supports:
       *
       * 1. [
       *   { id: 1, name: "HR" },
       *   { id: 2, name: "Training" }
       * ]
       *
       * 2. {
       *      data: [...]
       *    }
       *
       * 3. {
       *      content: [...]
       *    }
       */

      let departmentList = [];

      if (Array.isArray(data)) {
        departmentList = data;
      } else if (Array.isArray(data.data)) {
        departmentList = data.data;
      } else if (Array.isArray(data.content)) {
        departmentList = data.content;
      } else {
        departmentList = [];
      }

      setDepartments(departmentList);

    } catch (error) {
      console.error(
        "DEPARTMENT FETCH ERROR:",
        error
      );

      alert(
        "Unable to load departments. Please check department API."
      );

      setDepartments([]);

    } finally {
      setDepartmentLoading(false);
    }
  };

  // ==============================
  // Handle Input Change
  // ==============================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setEmployee((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ==============================
  // Register Employee
  // ==============================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!employee.departmentId) {
      alert("Please select a department.");
      return;
    }

    setLoading(true);

    try {
      // ======================================
      // Employee POST Request
      // ======================================
      const response = await fetch(
        "/api/employees",
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            name: employee.name,
            email: employee.email,
            phone: employee.phone,
            designation: employee.designation,

            // Department ID
            departmentId: Number(
              employee.departmentId
            ),

            experience: employee.experience,

            salary: employee.salary
              ? Number(employee.salary)
              : null,

            joiningDate: employee.joiningDate || null,

            location: employee.location,
          }),
        }
      );

      // ==============================
      // Error Handling
      // ==============================
      if (!response.ok) {
        let errorMessage =
          "Failed to register employee";

        try {
          const errorData =
            await response.json();

          console.error(
            "EMPLOYEE API ERROR:",
            errorData
          );

          if (errorData.message) {
            errorMessage =
              errorData.message;
          } else if (errorData.error) {
            errorMessage =
              errorData.error;
          }
        } catch {
          const errorText =
            await response.text();

          if (errorText) {
            errorMessage =
              errorText;
          }
        }

        throw new Error(errorMessage);
      }

      // ==============================
      // Saved Employee Response
      // ==============================
      const savedEmployee =
        await response.json();

      console.log(
        "EMPLOYEE SAVED:",
        savedEmployee
      );

      // ======================================
      // Employee ID
      // ======================================
      const employeeId =
        savedEmployee?.id ||
        savedEmployee?.employeeId ||
        savedEmployee?.data?.id ||
        savedEmployee?.data?.employeeId;

      console.log(
        "NEW EMPLOYEE ID:",
        employeeId
      );

      if (employeeId) {
        alert(
          `Employee registered successfully!\nEmployee ID: ${employeeId}`
        );
      } else {
        alert(
          "Employee registered successfully!"
        );
      }

      // ==============================
      // Redirect
      // ==============================
      navigate("/employee-list");

    } catch (error) {
      console.error(
        "EMPLOYEE SAVE ERROR:",
        error
      );

      alert(
        error.message ||
          "Failed to register employee. Please try again."
      );

    } finally {
      setLoading(false);
    }
  };

  // ==============================
  // UI
  // ==============================
  return (
    <div className="employee-register-page">

      {/* =========================
          HEADER
      ========================== */}
      <div className="employee-page-header">

        <div className="employee-header-left">

          <button
            type="button"
            className="employee-back-btn"
            onClick={() =>
              navigate("/employee-list")
            }
          >
            <ArrowLeft size={16} />
            Back
          </button>

          <div className="employee-title-icon">
            <UserPlus size={22} />
          </div>

          <div>
            <h1>
              Employee Registration
            </h1>

            <p>
              Add a new employee to your
              organization
            </p>
          </div>

        </div>

      </div>


      {/* =========================
          FORM CARD
      ========================== */}
      <div className="employee-form-card">

        {/* Card Header */}
        <div className="employee-card-header">

          <div className="employee-card-icon">
            <UserPlus size={20} />
          </div>

          <div>
            <h2>
              Employee Details
            </h2>

            <p>
              Enter employee information below
            </p>
          </div>

        </div>


        {/* =========================
            FORM
        ========================== */}
        <form onSubmit={handleSubmit}>

          <div className="employee-form-grid">


            {/* =========================
                NAME
            ========================== */}
            <div className="employee-form-group">

              <label>
                Employee Name{" "}
                <span>*</span>
              </label>

              <div className="employee-input-wrapper">

                <User size={17} />

                <input
                  type="text"
                  name="name"
                  value={employee.name}
                  onChange={handleChange}
                  placeholder="Enter employee name"
                  required
                />

              </div>

            </div>


            {/* =========================
                EMAIL
            ========================== */}
            <div className="employee-form-group">

              <label>
                Email{" "}
                <span>*</span>
              </label>

              <div className="employee-input-wrapper">

                <Mail size={17} />

                <input
                  type="email"
                  name="email"
                  value={employee.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  required
                />

              </div>

            </div>


            {/* =========================
                PHONE
            ========================== */}
            <div className="employee-form-group">

              <label>
                Phone{" "}
                <span>*</span>
              </label>

              <div className="employee-input-wrapper">

                <Phone size={17} />

                <input
                  type="tel"
                  name="phone"
                  value={employee.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  required
                />

              </div>

            </div>


            {/* =========================
                DESIGNATION
            ========================== */}
            <div className="employee-form-group">

              <label>
                Designation{" "}
                <span>*</span>
              </label>

              <div className="employee-input-wrapper">

                <Briefcase size={17} />

                <input
                  type="text"
                  name="designation"
                  value={employee.designation}
                  onChange={handleChange}
                  placeholder="e.g. Software Developer"
                  required
                />

              </div>

            </div>


            {/* =========================
                DEPARTMENT
            ========================== */}
            <div className="employee-form-group">

              <label>
                Department{" "}
                <span>*</span>
              </label>

              <div className="employee-input-wrapper">

                <Building2 size={17} />

                <select
                  name="departmentId"
                  value={employee.departmentId}
                  onChange={handleChange}
                  required
                  disabled={departmentLoading}
                >

                  <option value="">
                    {departmentLoading
                      ? "Loading Departments..."
                      : "Select Department"}
                  </option>

                  {departments.map(
                    (department) => (
                      <option
                        key={department.id}
                        value={department.id}
                      >
                        {department.name ||
                          department.departmentName ||
                          department.title}
                      </option>
                    )
                  )}

                </select>

              </div>

              {!departmentLoading &&
                departments.length === 0 && (
                  <small
                    style={{
                      color: "red",
                      marginTop: "5px",
                      display: "block",
                    }}
                  >
                    No departments found.
                  </small>
                )}

            </div>


            {/* =========================
                EXPERIENCE
            ========================== */}
            <div className="employee-form-group">

              <label>
                Experience
              </label>

              <div className="employee-input-wrapper">

                <Clock3 size={17} />

                <input
                  type="text"
                  name="experience"
                  value={employee.experience}
                  onChange={handleChange}
                  placeholder="e.g. 3 Years"
                />

              </div>

            </div>


            {/* =========================
                SALARY
            ========================== */}
            <div className="employee-form-group">

              <label>
                Salary
              </label>

              <div className="employee-input-wrapper">

                <IndianRupee size={17} />

                <input
                  type="number"
                  name="salary"
                  value={employee.salary}
                  onChange={handleChange}
                  placeholder="Enter salary"
                  min="0"
                />

              </div>

            </div>


            {/* =========================
                JOINING DATE
            ========================== */}
            <div className="employee-form-group">

              <label>
                Joining Date
              </label>

              <div className="employee-input-wrapper">

                <CalendarDays size={17} />

                <input
                  type="date"
                  name="joiningDate"
                  value={employee.joiningDate}
                  onChange={handleChange}
                />

              </div>

            </div>


            {/* =========================
                LOCATION
            ========================== */}
            <div className="employee-form-group">

              <label>
                Location
              </label>

              <div className="employee-input-wrapper">

                <MapPin size={17} />

                <input
                  type="text"
                  name="location"
                  value={employee.location}
                  onChange={handleChange}
                  placeholder="e.g. Noida"
                />

              </div>

            </div>

          </div>


          {/* =========================
              ACTION BUTTONS
          ========================== */}
          <div className="employee-form-actions">

            <button
              type="button"
              className="employee-cancel-btn"
              onClick={() =>
                navigate("/employee-list")
              }
              disabled={loading}
            >
              <ArrowLeft size={16} />
              Cancel
            </button>


            <button
              type="submit"
              className="employee-save-btn"
              disabled={
                loading ||
                departmentLoading ||
                departments.length === 0
              }
            >

              <Save size={16} />

              {loading
                ? "Registering..."
                : "Register Employee"}

            </button>

          </div>

        </form>

      </div>

    </div>
  );
}

export default EmployeeRegister;